import type { OperationMatrix } from "./engine";
import type { CoverageSummary } from "./coverage";
import { redactHeaders } from "./redact";
import { LIMITS } from "./limits";

export type ExportFormat = "markdown" | "json";

export interface ExportResult {
  filename: string;
  mimeType: string;
  content: string;
  truncated: boolean;
}

function severityLabel(severity: string): string {
  const labels: Record<string, string> = { info: "Info", warning: "Advertencia", error: "Error", critical: "Crítico" };
  return labels[severity] ?? severity;
}

function buildJsonExport(runId: string, scenarioId: string, matrix: OperationMatrix[], coverage: CoverageSummary): string {
  const payload = {
    runId,
    scenarioId,
    generatedAt: new Date().toISOString(),
    coverage,
    operations: matrix.map((operation) => ({
      operationId: operation.operationId,
      method: operation.method,
      path: operation.path,
      security: operation.security.map((s) => ({ schemeName: s.schemeName, type: s.type })),
      cases: operation.cases.map((failureCase) => {
        const example = operation.testExamples.find((candidate) => candidate.caseId === failureCase.id);
        return {
          statusCode: failureCase.statusCode,
          title: failureCase.title,
          documented: failureCase.documented,
          assumption: failureCase.assumption,
          accepted: failureCase.accepted,
          reviewed: failureCase.reviewed,
          justification: failureCase.justification,
          evidencePath: failureCase.evidencePath,
          testExample: example
            ? {
                method: example.method,
                url: example.url,
                headers: redactHeaders(example.headers),
                body: example.body,
                expectedStatus: example.expectedStatus,
                curl: example.curl,
              }
            : null,
        };
      }),
    })),
  };
  return JSON.stringify(payload, null, 2);
}

function buildMarkdownExport(runId: string, scenarioId: string, matrix: OperationMatrix[], coverage: CoverageSummary): string {
  const lines: string[] = [];
  lines.push(`# REST Failure Matrix — plan de pruebas negativo`);
  lines.push("");
  lines.push(`- **Run:** \`${runId}\``);
  lines.push(`- **Escenario:** \`${scenarioId}\``);
  lines.push(`- **Cobertura negativa:** ${coverage.overall.percentage}% (${coverage.overall.covered}/${coverage.overall.relevant} casos revisados)`);
  lines.push(`- **Generado:** ${new Date().toISOString()}`);
  lines.push("");
  lines.push("> Los casos marcados como *supuesto* fueron inferidos por el motor a partir del contrato y requieren revisión humana antes de tratarse como especificación.");
  lines.push("");

  for (const operation of matrix) {
    lines.push(`## ${operation.method.toUpperCase()} ${operation.path}`);
    if (operation.summary) lines.push(operation.summary);
    if (operation.security.length > 0) {
      lines.push(`**Seguridad:** ${operation.security.map((s) => `${s.schemeName} (${s.type})`).join(", ")}`);
    } else {
      lines.push("**Seguridad:** ninguna declarada.");
    }
    lines.push("");
    lines.push("| Código | Caso | Origen | Revisado | Justificación |");
    lines.push("|---|---|---|---|---|");
    for (const failureCase of operation.cases) {
      const origin = failureCase.documented ? "documentado" : failureCase.assumption ? "supuesto" : "inferido";
      lines.push(
        `| ${failureCase.statusCode} | ${failureCase.title} (${severityLabel(failureCase.severity)}) | ${origin} | ${failureCase.reviewed ? "sí" : "no"} | ${failureCase.justification.replace(/\|/g, "\\|")} |`,
      );
    }
    lines.push("");
    for (const example of operation.testExamples) {
      const failureCase = operation.cases.find((candidate) => candidate.id === example.caseId);
      lines.push(`<details><summary>Ejemplo ${example.expectedStatus} — ${failureCase?.title ?? ""}</summary>`);
      lines.push("");
      lines.push("```bash");
      lines.push(example.curl);
      lines.push("```");
      lines.push("");
      lines.push("</details>");
      lines.push("");
    }
  }

  return lines.join("\n");
}

/**
 * Genera el contenido exportable de la matriz (P8-R2/P8-R4). El resultado
 * excluye siempre payloads privados reales (los ejemplos son sintéticos) y
 * redacta cabeceras sensibles. Se descarga mediante Blob + atributo `download`
 * (equivalente a `Content-Disposition: attachment` en una app 100% estática
 * sin servidor propio que fije esa cabecera).
 */
export function exportTests(
  runId: string,
  scenarioId: string,
  matrix: OperationMatrix[],
  coverage: CoverageSummary,
  format: ExportFormat,
): ExportResult {
  const content = format === "json" ? buildJsonExport(runId, scenarioId, matrix, coverage) : buildMarkdownExport(runId, scenarioId, matrix, coverage);
  const truncated = content.length > LIMITS.maxExportBytes;
  const finalContent = truncated ? content.slice(0, LIMITS.maxExportBytes) : content;
  return {
    filename: format === "json" ? `${runId}.json` : `${runId}.md`,
    mimeType: format === "json" ? "application/json" : "text/markdown",
    content: finalContent,
    truncated,
  };
}
